import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { API_BASE_URL } from '../config/api-base-url.token';
import type {
  AuthSuccessResponse,
  ForgotPasswordRequestOtpPayload,
  ForgotPasswordRequestOtpResponse,
  ForgotPasswordResendOtpPayload,
  ForgotPasswordResendOtpResponse,
  ForgotPasswordResetPayload,
  ForgotPasswordResetResponse,
  ForgotPasswordVerifyOtpPayload,
  ForgotPasswordVerifyOtpResponse,
  AuthUser,
  LoginPayload,
  ResendSignupOtpPayload,
  SignupOtpResentResponse,
  RefreshResponse,
  SignupPayload,
  SignupVerificationRequiredResponse,
  UserPreferences,
  VerifySignupOtpPayload,
} from '../models/auth.models';

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  signup(payload: SignupPayload): Observable<SignupVerificationRequiredResponse> {
    return this.http.post<SignupVerificationRequiredResponse>(`${this.baseUrl}/auth/signup`, payload, {
      withCredentials: true,
    });
  }

  verifySignupOtp(payload: VerifySignupOtpPayload): Observable<AuthSuccessResponse> {
    return this.http.post<AuthSuccessResponse>(`${this.baseUrl}/auth/signup/verify-otp`, payload, {
      withCredentials: true,
    });
  }

  resendSignupOtp(payload: ResendSignupOtpPayload): Observable<SignupOtpResentResponse> {
    return this.http.post<SignupOtpResentResponse>(`${this.baseUrl}/auth/signup/resend-otp`, payload, {
      withCredentials: true,
    });
  }

  forgotPasswordRequestOtp(payload: ForgotPasswordRequestOtpPayload): Observable<ForgotPasswordRequestOtpResponse> {
    return this.http.post<ForgotPasswordRequestOtpResponse>(`${this.baseUrl}/auth/forgot-password/request-otp`, payload, {
      withCredentials: true,
    });
  }

  forgotPasswordResendOtp(payload: ForgotPasswordResendOtpPayload): Observable<ForgotPasswordResendOtpResponse> {
    return this.http.post<ForgotPasswordResendOtpResponse>(`${this.baseUrl}/auth/forgot-password/resend-otp`, payload, {
      withCredentials: true,
    });
  }

  forgotPasswordVerifyOtp(payload: ForgotPasswordVerifyOtpPayload): Observable<ForgotPasswordVerifyOtpResponse> {
    return this.http.post<ForgotPasswordVerifyOtpResponse>(`${this.baseUrl}/auth/forgot-password/verify-otp`, payload, {
      withCredentials: true,
    });
  }

  forgotPasswordReset(payload: ForgotPasswordResetPayload): Observable<ForgotPasswordResetResponse> {
    return this.http.post<ForgotPasswordResetResponse>(`${this.baseUrl}/auth/forgot-password/reset`, payload, {
      withCredentials: true,
    });
  }

  login(payload: LoginPayload): Observable<AuthSuccessResponse> {
    return this.http.post<AuthSuccessResponse>(`${this.baseUrl}/auth/login`, payload, {
      withCredentials: true,
    });
  }

  refresh(refreshToken?: string): Observable<RefreshResponse> {
    return this.http.post<RefreshResponse>(`${this.baseUrl}/auth/refresh`, {}, {
      withCredentials: true,
      headers: refreshToken
        ? {
            Authorization: `Bearer ${refreshToken}`,
          }
        : undefined,
    });
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/logout`, {}, { withCredentials: true });
  }

  me(): Observable<AuthUser> {
    return this.http.get<AuthUser>(`${this.baseUrl}/auth/me`);
  }

  updatePreferences(payload: UserPreferences): Observable<AuthUser> {
    return this.http.patch<AuthUser>(`${this.baseUrl}/auth/me/preferences`, payload, {
      withCredentials: true,
    });
  }
}
